<template>
  <NotificationSettingRow>
    <template #title>
      <label class="mb-0" :for="switchId">Weekly auto-send</label>
    </template>
    <template #description>
      <p>Sends pending notifications every Friday at 17:00 (Estonian time).</p>
      <p>
        <font-awesome-icon icon="clock" />
        Last automatic run: {{ lastRunText }}
      </p>
    </template>
    <template #control>
      <div
        class="form-check form-switch mb-0"
        :title="canEdit ? undefined : 'Only super admins can change this.'"
      >
        <input
          :id="switchId"
          type="checkbox"
          class="form-check-input"
          role="switch"
          :checked="enabled"
          :disabled="isLoading || isUpdating || !canEdit"
          @change="updateEnabled(($event.target as HTMLInputElement).checked)"
        />
      </div>
    </template>
  </NotificationSettingRow>
</template>

<script setup lang="ts">
import { formatDateTime } from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { computed, onMounted, ref, useId } from "vue";
import NotificationSettingRow from "./NotificationSettingRow.vue";

/**
 * Arms or disarms the Friday-evening auto-release of pending transaction notification emails
 * (`transaction-notification-weekly-autosend` beat job) and shows what the last run did.
 * Self-contained: owns its GET/PUT against the toggle resource, so the transactions view only
 * has to place it. Sales admins can read the state (SALES_READ) but only super admins may write
 * (SUPER); the server reports `can_edit` for the caller's effective ceiling (so an impersonating
 * super admin keeps control) and the switch disables itself on it instead of failing on the PUT.
 */

interface AutosendLastRun {
  ran_at: string;
  outcome: "sent" | "skipped" | "already_running" | "failed";
  company_count: number | null;
  error: string | null;
}

interface AutosendState {
  enabled: boolean;
  last_run: AutosendLastRun | null;
  can_edit: boolean;
}

const AUTOSEND_URL = "/api/admin/transaction-notification-autosend";

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const switchId = useId();

const enabled = ref(false);
const canEdit = ref(false);
const lastRun = ref<AutosendLastRun | null>(null);
const isLoading = ref(true);
const isUpdating = ref(false);

const lastRunText = computed(() => {
  const run = lastRun.value;
  if (!run) {
    return "never";
  }
  const when = formatDateTime(run.ran_at);
  if (run.outcome === "sent") {
    return `${when}: sent to ${run.company_count ?? 0} companies`;
  }
  if (run.outcome === "skipped") {
    return `${when}: skipped (auto-send was off)`;
  }
  if (run.outcome === "already_running") {
    return `${when}: skipped (a manual release was already running)`;
  }
  return `${when}: failed${run.error ? ` (${run.error})` : ""}`;
});

onMounted(async () => {
  try {
    const response = await goSolidApi.get<AutosendState>(AUTOSEND_URL);
    applyState(response.data);
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
});

const applyState = (state: AutosendState) => {
  enabled.value = state.enabled;
  lastRun.value = state.last_run;
  canEdit.value = state.can_edit;
};

const updateEnabled = async (next: boolean) => {
  if (!canEdit.value) {
    return;
  }
  // Optimistic: the switch has already moved; a failed PUT flips the ref back so it re-renders.
  enabled.value = next;
  isUpdating.value = true;
  try {
    const response = await goSolidApi.put<AutosendState>(AUTOSEND_URL, {
      enabled: next,
    });
    applyState(response.data);
    globalSuccessHandler(
      next ? "Auto-send enabled" : "Auto-send disabled",
      next
        ? "Pending transaction notifications will be sent every Friday at 17:00 Estonian time."
        : "Nothing will be sent automatically until auto-send is switched back on."
    );
  } catch (error: unknown) {
    enabled.value = !next;
    globalErrorHandler(error);
  } finally {
    isUpdating.value = false;
  }
};
</script>
